import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { ControlResultSlideToggleComponent } from '@connect-services/client/components/control-result-slide-toggle/control-result-slide-toggle.component';
import { FIELD_INTERVENTION_CONTROL_RESULTS_COLUMN_WIDTHS } from '@connect-services/client/components/field-intervention-form-entity-control-results/field-intervention-form-entity-control-results.component';
import { ImageModalComponent } from '@connect-services/client/components/image-modal/image-modal.component';
import { FieldInterventionControlResultDetails } from '@connect-services/client/services/field-intervention.service';
import { ButtonComponent, IconPosition } from '@connect-services/src/lib/components/button/button.component';
import {
    CreateFieldInterventionControlDto,
    FieldInterventionControlResult,
    FieldInterventionControlResultPhotoMetadataDto,
    FieldInterventionControlResultsService,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, ControlResultSlideToggleComponent, LoadingComponent, RessourcesModule],
    selector: 'app-field-intervention-form-entity-control-results-content',
    styleUrl: './../field-intervention-form-entity-control-results.component.scss',
    templateUrl: './field-intervention-form-entity-control-results-content.component.html',
})
export class FieldInterventionFormEntityControlResultsContentComponent {
    public readonly controlResult = input.required<FieldInterventionControlResultDetails>();
    public readonly validityChanged = output<FieldInterventionControlResult.ValidationEnum>();

    protected readonly columnWidthClasses = FIELD_INTERVENTION_CONTROL_RESULTS_COLUMN_WIDTHS;
    protected readonly FormatEnum = CreateFieldInterventionControlDto.FormatEnum;
    protected readonly IconPosition = IconPosition;
    protected readonly isLoadingImages = signal<boolean>(false);

    private readonly loadedImages = signal<string[]>([]);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly fieldInterventionControlResultsService = inject(FieldInterventionControlResultsService);
    private readonly matDialog = inject(MatDialog);

    protected async openImageDialog(controlResultDetails: FieldInterventionControlResultDetails): Promise<void> {
        if (this.isLoadingImages()) {
            return;
        }

        if (controlResultDetails.results?.length) {
            this.openDialog(controlResultDetails.results, controlResultDetails.controlLabel);

            return;
        }

        if (this.loadedImages().length) {
            this.openDialog(this.loadedImages(), controlResultDetails.controlLabel);

            return;
        }

        if (!controlResultDetails.photoCount) {
            return;
        }

        this.isLoadingImages.set(true);

        try {
            const images: string[] = await this.fetchControlResultPhotos(controlResultDetails.id);

            if (!images.length) {
                return;
            }

            this.loadedImages.set(images);
            this.openDialog(images, controlResultDetails.controlLabel);
        } finally {
            this.isLoadingImages.set(false);
        }
    }

    private async fetchControlResultPhotos(controlResultId: string): Promise<string[]> {
        const photos: FieldInterventionControlResultPhotoMetadataDto[] = await firstValueFrom(
            this.fieldInterventionControlResultsService.fieldInterventionControlResultControllerGetPhotos({
                controlResultId,
            }),
        );

        return Promise.all(
            photos.map(async (photo: FieldInterventionControlResultPhotoMetadataDto) => {
                const blob = await firstValueFrom(
                    this.fieldInterventionControlResultsService.fieldInterventionControlResultControllerGetPhoto({
                        controlResultId,
                        photoId: photo.id,
                    }),
                );

                return URL.createObjectURL(blob);
            }),
        );
    }

    private openDialog(images: string[], title: string): void {
        this.matDialog.open(ImageModalComponent, {
            data: { images, title },
            width: '50vw',
        });
    }
}
